import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardCopy, Download, Loader2, MoreVertical } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { smallTextSize, tertiaryText } from '../../../styling/stylingHelper';

/**
 * The "..." actions menu next to the Revenue Items time controls: copy the table to the
 * clipboard, or download it as .json / .txt / .xlsx. Every action exports exactly the
 * rows on screen — the page owns that data and hands it over through these callbacks.
 *
 * Sits behind the same permission as the cost price automation actions (see the page).
 */

interface RevenueItemsExportMenuProps {
  /** Copy handlers resolve true on success, so the menu can confirm with a checkmark. */
  onCopyText: () => Promise<boolean>;
  onCopyJson: () => Promise<boolean>;
  onDownloadJson: () => void;
  onDownloadTxt: () => void;
  /** Excel loads exceljs on demand, so it can take a moment — the button shows a spinner. */
  onDownloadExcel: () => Promise<void>;
}

type CopyTarget = 'text' | 'json';

const itemClass =
  'w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-medium text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark';

const RevenueItemsExportMenu: React.FC<RevenueItemsExportMenuProps> = ({
  onCopyText,
  onCopyJson,
  onDownloadJson,
  onDownloadTxt,
  onDownloadExcel,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [justCopied, setJustCopied] = useState<CopyTarget | null>(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Reset the "Copied" confirmation shortly after it shows.
  useEffect(() => {
    if (!justCopied) return;
    const timer = setTimeout(() => setJustCopied(null), 2000);
    return () => clearTimeout(timer);
  }, [justCopied]);

  // The menu stays open on copy so the checkmark is visible; downloads close it.
  const handleCopy = async (target: CopyTarget) => {
    const success = await (target === 'text' ? onCopyText() : onCopyJson());
    if (success) setJustCopied(target);
  };

  const handleDownload = (download: () => void) => {
    setIsOpen(false);
    download();
  };

  const handleExcel = async () => {
    if (isExportingExcel) return;
    setIsOpen(false);
    setIsExportingExcel(true);
    try {
      await onDownloadExcel();
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isExportingExcel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('revenueHistory.export.menuLabel')}
        className={cn(
          'flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors',
          isExportingExcel && 'opacity-50 cursor-not-allowed'
        )}
      >
        {isExportingExcel ? <Loader2 className="w-5 h-5 animate-spin" /> : <MoreVertical className="w-5 h-5" />}
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[240px] rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg py-1 z-50"
        >
          <div className={cn(tertiaryText, smallTextSize, 'px-3 py-1.5')}>
            {t('revenueHistory.export.currentViewNote')}
          </div>

          <button type="button" role="menuitem" onClick={() => handleCopy('text')} className={itemClass}>
            {justCopied === 'text' ? <Check className="w-4 h-4 shrink-0 text-[#0E8965] dark:text-darkGreenColor-dark" /> : <ClipboardCopy className="w-4 h-4 shrink-0" />}
            {justCopied === 'text' ? t('revenueHistory.export.copied') : t('revenueHistory.export.copyText')}
          </button>
          <button type="button" role="menuitem" onClick={() => handleCopy('json')} className={itemClass}>
            {justCopied === 'json' ? <Check className="w-4 h-4 shrink-0 text-[#0E8965] dark:text-darkGreenColor-dark" /> : <ClipboardCopy className="w-4 h-4 shrink-0" />}
            {justCopied === 'json' ? t('revenueHistory.export.copied') : t('revenueHistory.export.copyJson')}
          </button>

          <div className="my-1 border-t border-[#E5E7EB] dark:border-borderPrimary-dark" />

          <button type="button" role="menuitem" onClick={() => handleDownload(onDownloadJson)} className={itemClass}>
            <Download className="w-4 h-4 shrink-0" />
            {t('revenueHistory.export.downloadJson')}
          </button>
          <button type="button" role="menuitem" onClick={() => handleDownload(onDownloadTxt)} className={itemClass}>
            <Download className="w-4 h-4 shrink-0" />
            {t('revenueHistory.export.downloadTxt')}
          </button>
          <button type="button" role="menuitem" onClick={handleExcel} className={itemClass}>
            <Download className="w-4 h-4 shrink-0" />
            {t('revenueHistory.export.downloadExcel')}
          </button>
        </div>
      )}
    </div>
  );
};

export default RevenueItemsExportMenu;
